import React, { FC, useCallback } from 'react';
import { LoanFormat } from '../../../models/LoanFormat';
import { ProductType } from '../../../models/ProductType';
import {
    invalidateAllProductsByProductTypeProducts,
    useAllProductsByProductTypeProducts
} from '../../api/all-products/useAllProductsByProductTypeProducts';
import { useParams } from 'react-router';
import { getLoanFormat } from '../../shared/utils/getLoanFormat';
import { ProductSorting } from '../../../models/Product';
import { ContainerInnerWithColumns } from '../../shared/common/ContainerInner';
import { LoadingWrapper } from '../../shared/components/LoadingWrapper';
import LoadingOverlay from '../../shared/components/LoadingOverlay';
import FilterBar from '../../shared/common/FilterBar';
import { ResponsiveFilterBar } from '../../shared/filter/ResponsiveFilterBar';
import ProductList from '../../shared/common/ProductList';
import Pagination from '../../shared/common/Pagination';
import NoElementsFound, { NO_ELEMENTS_PRODUCTS } from '../../shared/components/NoElementsFound';
import { ProductSelectionHeader } from '../../shared/product-selection/ProductSelectionHeader';
import { BulkActionButtons } from '../../shared/search/BulkActionButtons';
import { CatalogueMenuActionButtons } from '../catalogue/CatalogueMenuActionButtons';
import { MAX_SELECT, useProductSelection } from '../../shared/product-selection/useProductSelection';
import { fetchAllProductsByProductTypeAggregationsQuickSearch } from '../../api/all-products/fetchAllProductsByProductTypeAggregationsQuickSearch';
import { useAddBulkToCart } from './useAddBulkToCart';
import { SelectionList } from '../../../models/SelectionList';
import { PriceTier } from '../../../models/PriceTier';
import { ExcludeProductSelection } from '../../shared/product-selection/ProductSelectionTypes';
import { QueryKeyBuilder } from '../../shared/filter/useAggregationsQuery';
import { FetchAggregations } from '../../api/listContent/fetchAggregations';
import { fetchAllProductsByProductTypeAggregations } from '../../api/all-products/fetchAllProductsByProductTypeAggregations';
import { FetchAggregationsQuickSearch } from '../../api/listContent/fetchAggregationsQuickSearch';
import { useDefaultVisibleFilterGroups } from '../../shared/filter/defaultVisibleFilterGroups';
import { useTranslation } from 'react-i18next';
import { useAddBulkToSelectionlist } from './useAddBulkToSelectionlist';
import { TYPE_WARNING } from '../../shared/notification/actions';
import { useAddToList } from '../../api/selection-list/useAddSingleToList';
import { useAddToCart } from '../../api/shopping-cart/useAddToCart';
import { CheckBeforeAddingModal } from '../../shared/common/CheckBeforeAddingModal';
import { SingleAddOptions } from '../../../models/ListContentCheckOptions';
import { ListActionTarget } from '../../../models/ListActionTarget';
import { useListContentChecks } from '../../shared/common/useListContentChecks';
import { useAgeCategorySearchParam } from '../../shared/utils/useAgeCategorySearchParam';
import { SubscriptionPeriodType } from '../../../models/Article';
import { nothingSelected } from '../../shared/product-selection/ProductSelectionUtils';
import { UnavailableProductsModal } from '../../shared/common/UnavailableProductsModal';
import { useSelectionLists } from '../../api/selection-list/useSelectionLists';
import { useCreateNewSelectionList } from '../../api/selection-list/useCreateNewSelectionList';
import { useDisplayTypeSearchParam } from '../../shared/utils/useDisplayTypeSearchParam';
import { useDisplayFilterSearchParam } from '../../shared/utils/useDisplayFilterSearchParam';
import { LOANFORMAT_EPRESS, LOANFORMAT_EPRESS_OBJECT } from '../../shared/loanFormat/constants';
import CatalogueListItem from '../catalogue/CatalogueListItem';
import { CatalogueProductTile } from '../catalogue/CatalogueProductTile';
import { showNotification } from '../../shared/notification/toastNotification';
import { usePaginationAndSortParams } from '../../shared/hooks/usePaginationAndSortParams';
import { useProductFilters } from '../../shared/filter/useProductFilters';
import { useCheckUsageConditions } from '../../api/all-products/useCheckUsageConditions';

export const ProductTypePage: FC<{ productType: ProductType; loanFormat: LoanFormat }> = ({
    loanFormat,
    productType
}) => {
    const { t } = useTranslation();
    const { productSelection, toggleSelectionMode, onSelectButtonClick, selectAll, cancelSelection } =
        useProductSelection();

    const { selectionLists } = useSelectionLists();

    const { shopLanguage } = useParams<'shopLanguage'>();

    const loanFormatObject = getLoanFormat(loanFormat.toLowerCase());

    const { displayFilter } = useDisplayFilterSearchParam();
    const { page, sortBy, pageSize } = usePaginationAndSortParams({
        defaultPageSize: 54,
        defaultSortBy: ProductSorting.RELEASE_DATE_NEW_TO_OLD
    });

    const { productFilter } = useProductFilters();
    const { value: ageCategory } = useAgeCategorySearchParam();
    const { display } = useDisplayTypeSearchParam();

    const { data: allProducts, isLoading: isLoadingProducts } = useAllProductsByProductTypeProducts({
        loanFormat: loanFormatObject.requestKey ?? 'UNKNOWN',
        ageCategory,
        pagination: { sortBy, page, pageSize },
        filterMap: productFilter,
        productType,
        shopLanguage
    });

    const { isLoading: isLoadingAddProductsToCartByFilter, addToCartBulk: addBulkProductSelectionToCart } =
        useAddBulkToCart({
            loanFormat: loanFormatObject,
            productType,
            ageCategory,
            shopLanguage,
            productFilter,
            onSuccess: () => {
                invalidateAllProductsByProductTypeProducts(
                    loanFormatObject.requestKey ?? 'UNKNOWN',
                    productType
                ).then();
                cancelSelection();
            }
        });

    const { isLoading: isLoadingAddProductsToSelectionlistByFilter, addToListBulk: addBulkProductSelectionToList } =
        useAddBulkToSelectionlist({
            productFilter,
            loanFormat: loanFormatObject,
            productType,
            ageCategory,
            shopLanguage,
            onSuccess: () => {
                cancelSelection();
            }
        });

    const onBulkAddToList = (
        list: SelectionList,
        priceTier: PriceTier,
        subscriptionPeriodType: SubscriptionPeriodType,
        performChecks: boolean
    ) => {
        const productSelection: ExcludeProductSelection = {
            excluded: [],
            totalCount: allProducts?.totalHits ?? 0
        };

        if (productSelection.totalCount > MAX_SELECT) {
            showNotification(t('notifications.useProductSelection.error', { maxSelect: MAX_SELECT }), TYPE_WARNING);
            throw new Error('max selection exceeded');
        }

        return addBulkProductSelectionToList(
            list.id,
            priceTier,
            subscriptionPeriodType,
            performChecks,
            productSelection
        );
    };

    const onBulkAddToCart = async (
        priceTier: PriceTier,
        subscriptionPeriodType: SubscriptionPeriodType,
        performChecks: boolean
    ) => {
        const productSelection: ExcludeProductSelection = {
            excluded: [],
            totalCount: allProducts?.totalHits ?? 0
        };

        if (productSelection.totalCount > MAX_SELECT) {
            showNotification(t('notifications.useProductSelection.error', { maxSelect: MAX_SELECT }), TYPE_WARNING);
            throw new Error('max selection exceeded');
        }

        return addBulkProductSelectionToCart(priceTier, subscriptionPeriodType, performChecks, productSelection);
    };

    //region FILTERBAR_V2
    const visibleFilterGroups = useDefaultVisibleFilterGroups();
    const aggregationsQueryKeyBuilder = useCallback<QueryKeyBuilder>(
        ({ filters, expandedFilterGroups }) => [
            'byGenrePage',
            loanFormatObject.requestKey!,
            productType,
            'aggregation',
            ageCategory,
            filters,
            expandedFilterGroups,
            shopLanguage
        ],
        [ageCategory, loanFormatObject.requestKey, productType, shopLanguage]
    );
    const fetchAggregations = useCallback<FetchAggregations>(
        options =>
            fetchAllProductsByProductTypeAggregations({
                ...options,
                loanFormat: loanFormatObject.requestKey!,
                ageCategory,
                productType,
                shopLanguage
            }),
        [ageCategory, loanFormatObject.requestKey, productType, shopLanguage]
    );
    const fetchAggregationsQuickSearch = useCallback<FetchAggregationsQuickSearch>(
        options =>
            fetchAllProductsByProductTypeAggregationsQuickSearch({
                ...options,
                loanFormat: loanFormatObject.requestKey!,
                ageCategory,
                productType,
                shopLanguage
            }),
        [ageCategory, loanFormatObject.requestKey, productType, shopLanguage]
    );
    //endregion

    const { data: hasSingleUsageCondition } = useCheckUsageConditions(fetchAggregations, aggregationsQueryKeyBuilder);

    const { addToList } = useAddToList();
    const { addToCart } = useAddToCart({
        callback: () =>
            invalidateAllProductsByProductTypeProducts(loanFormatObject.requestKey ?? 'UNKNOWN', productType).then()
    });

    const { mutate: createSelectionList } = useCreateNewSelectionList();

    const onAction = useCallback(
        (performChecks: boolean, options: SingleAddOptions) => {
            if (options.target === ListActionTarget.List) {
                return addToList(options.options, options.listId, performChecks);
            } else {
                return addToCart(options.options, performChecks);
            }
        },
        [addToCart, addToList]
    );

    const { changePrice, getCheckBeforeAddingModalProps, getUnavailableProductsModalProps } =
        useListContentChecks<SingleAddOptions>({ onAction });

    const products = allProducts?.hits ?? [];
    const totalProducts = allProducts?.totalHits ?? 0;

    const loading =
        isLoadingProducts || isLoadingAddProductsToCartByFilter || isLoadingAddProductsToSelectionlistByFilter;

    return (
        <ContainerInnerWithColumns hasTopMargin={false}>
            {loading ? (
                <LoadingWrapper>
                    <LoadingOverlay loading={loading} />
                </LoadingWrapper>
            ) : null}

            <ProductSelectionHeader
                header={
                    products.length > 0 || loading ? (
                        <BulkActionButtons
                            listLabel={t('addToList')}
                            cartLabel={t('addToCart')}
                            selectionLists={selectionLists}
                            onCreateNewListClicked={createSelectionList}
                            onBulkAddToList={onBulkAddToList}
                            onBulkAddToCart={onBulkAddToCart}
                            onlyEpressProducts={loanFormatObject.requestKey === LOANFORMAT_EPRESS_OBJECT.requestKey}
                            hasSingleUsageCondition={hasSingleUsageCondition}
                        />
                    ) : null
                }
                menu={
                    <CatalogueMenuActionButtons
                        onBulkAddToCart={(priceTier, subscriptionPeriodType, performChecks) =>
                            addBulkProductSelectionToCart(
                                priceTier,
                                subscriptionPeriodType,
                                performChecks,
                                productSelection
                            )
                        }
                        onBulkAddToList={(list, priceTier, subscriptionPeriodType, performChecks) =>
                            addBulkProductSelectionToList(
                                list.id,
                                priceTier,
                                subscriptionPeriodType,
                                performChecks,
                                productSelection
                            )
                        }
                        onlyEpressProducts={loanFormatObject.requestKey === LOANFORMAT_EPRESS_OBJECT.requestKey}
                        defaultPriceTier={
                            loanFormatObject.requestKey === LOANFORMAT_EPRESS
                                ? { lendingModel: 'SIM', level: 'MIN' }
                                : { lendingModel: 'OCOU', level: 'MIN' }
                        }
                        isNothingSelected={nothingSelected(productSelection)}
                        hasSingleUsageCondition={hasSingleUsageCondition}
                    />
                }
            />

            <FilterBar
                sortBy={sortBy}
                totalProducts={totalProducts}
                productSelection={productSelection}
                pageSize={pageSize}
                displayType={display}
                hideGroupBy={true}
                toggleSelectionMode={toggleSelectionMode}
                selectAll={() => selectAll(totalProducts)}
            />

            <div className="content-area-inner flex-ca">
                {displayFilter ? (
                    <ResponsiveFilterBar
                        visibleFilterGroups={visibleFilterGroups}
                        buildQueryKey={aggregationsQueryKeyBuilder}
                        fetchAggregations={fetchAggregations}
                        fetchAggregationsQuickSearch={fetchAggregationsQuickSearch}
                    />
                ) : null}
                {products.length > 0 || loading ? (
                    <div className="ca-main">
                        <div className="ca-main-inner">
                            <ProductList
                                products={products}
                                onAddPrice={changePrice}
                                displayType={display}
                                productSelection={productSelection}
                                onSelectProduct={onSelectButtonClick}
                                isProductSelectable={true}
                            >
                                {({ key, isListView, ...value }) =>
                                    isListView ? (
                                        <CatalogueListItem {...value} key={key} shopLanguage={shopLanguage} />
                                    ) : (
                                        <CatalogueProductTile {...value} key={key} shopLanguage={shopLanguage} />
                                    )
                                }
                            </ProductList>
                            <Pagination totalItems={totalProducts} pageSize={pageSize} />
                        </div>
                    </div>
                ) : (
                    <NoElementsFound type={NO_ELEMENTS_PRODUCTS} />
                )}
            </div>
            <CheckBeforeAddingModal {...getCheckBeforeAddingModalProps()} />
            <UnavailableProductsModal {...getUnavailableProductsModalProps()} />
        </ContainerInnerWithColumns>
    );
};
